import { DOW_LABELS, addDays, dayLabel, dayMonth, parseDay, weekKey } from "./date";
import { dayMark, type DayMark } from "./dayMarks";
import { basedOn, type Insight } from "./insight";
import { MEALS, MEAL_LABEL, entriesFor, foodOn, proteinTarget, totalsOf } from "./intake";
import type { KayaState, MealSlot } from "./types";

/**
 * Food by week, on complete days only (ADR-049; research user-demand.md §5 #3).
 *
 * A complete day is one with food logged, food tracking on, and no mark on it. An untracked day
 * (some of it not logged) and a fast day (a fast the person chose) are left out of every average;
 * so is a day with nothing logged. None of them is ever counted as zero — the averages are what
 * was eaten on the days logged in full, and a week with no complete day has no average at all
 * (null, invariant 11).
 */

const DAY_NAME = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Whether a day may join a food average: logged, tracking on, and not marked. */
export function completeDay(S: KayaState, ds: string): boolean {
  return foodOn(S) && dayMark(S, ds) === null && entriesFor(S, ds).length > 0;
}

export interface WeekDay {
  ds: string;
  /** kcal logged, whatever the day's mark; null with nothing logged */
  kcal: number | null;
  protein: number | null;
  /** the meals something was logged in, in meal order */
  meals: MealSlot[];
  mark: DayMark | null;
  complete: boolean;
  travel: boolean;
  /** the day's budget, when the body numbers give one */
  budget: number | null;
  /** kcal over the budget on a complete day; null when within it or not judged */
  over: number | null;
}

export interface FoodWeek {
  /** the Monday */
  from: string;
  /** the last day shown: the Sunday, or `end` for the week in progress */
  to: string;
  /** true while the week has days still to come */
  current: boolean;
  days: WeekDay[];
  complete: number;
  fast: number;
  untracked: number;
  /** days with nothing logged and no mark */
  unlogged: number;
  /** means over the complete days; null when there are none */
  avgKcal: number | null;
  avgProtein: number | null;
  /** the protein target the complete days were eaten against, averaged */
  proteinTarget: number | null;
  /** the complete days' budgets, averaged; null without the body numbers */
  budget: number | null;
  /** complete days with a budget, and those at or under it */
  judged: number;
  within: number;
}

const mean = (xs: number[]): number | null => (xs.length ? xs.reduce((a, x) => a + x, 0) / xs.length : null);

/** Monday of the week `ds` falls in. */
export function weekOf(ds: string): string {
  return weekKey(parseDay(ds));
}

/**
 * One week, Monday to Sunday, through `end` at the latest — Progress › Food reads up to
 * yesterday, because a half-logged today would drag every average. Days before the start date
 * could never have been logged and are not listed. `budget` is the page's (`dayLedger`), passed
 * in so the week and the rest of Progress judge a day by the same figure.
 */
export function foodWeek(
  S: KayaState,
  monday: string,
  end: string,
  budget: (ds: string) => number | null
): FoodWeek {
  const sunday = addDays(monday, 6);
  const to = sunday < end ? sunday : end;
  const start = S.profile.startDate && /^\d{4}-\d{2}-\d{2}$/.test(S.profile.startDate) ? S.profile.startDate : null;
  const days: WeekDay[] = [];
  for (let i = 0; i < 7; i++) {
    const ds = addDays(monday, i);
    if (ds > to) break;
    if (start && ds < start) continue;
    const entries = foodOn(S) ? entriesFor(S, ds) : [];
    const t = totalsOf(entries);
    const complete = completeDay(S, ds);
    const b = complete ? budget(ds) : null;
    days.push({
      ds,
      kcal: entries.length ? t.kcal : null,
      protein: entries.length ? Math.round(t.p) : null,
      meals: MEALS.filter((m) => entries.some((e) => e.meal === m)),
      mark: dayMark(S, ds),
      complete,
      travel: !!S.travelDates?.[ds],
      budget: b,
      over: b !== null && Number.isFinite(b) && t.kcal > b ? t.kcal - b : null,
    });
  }

  const full = days.filter((d) => d.complete);
  const judged = full.filter((d) => d.budget !== null && Number.isFinite(d.budget));
  const avgK = mean(full.map((d) => d.kcal ?? 0));
  const avgP = mean(full.map((d) => d.protein ?? 0));
  const targetP = mean(full.map((d) => S.logs[d.ds]?.targets?.p ?? proteinTarget(S)));
  const avgB = mean(judged.map((d) => d.budget ?? 0));
  return {
    from: monday,
    to,
    current: to < sunday,
    days,
    complete: full.length,
    fast: days.filter((d) => d.mark === "fast").length,
    untracked: days.filter((d) => d.mark === "untracked").length,
    unlogged: days.filter((d) => d.mark === null && !d.complete).length,
    avgKcal: avgK === null ? null : Math.round(avgK),
    avgProtein: avgP === null ? null : Math.round(avgP),
    proteinTarget: targetP === null ? null : Math.round(targetP),
    budget: avgB === null ? null : Math.round(avgB),
    judged: judged.length,
    within: judged.filter((d) => d.over === null).length,
  };
}

/**
 * The weeks of the last 28 days ending `end`, newest first — at most four, the week in progress
 * first. Weeks with no day at or after the start date are dropped.
 */
export function recentWeeks(
  S: KayaState,
  end: string,
  budget: (ds: string) => number | null,
  count = 4
): FoodWeek[] {
  const out: FoodWeek[] = [];
  let monday = weekOf(end);
  for (let i = 0; i < count; i++) {
    const w = foodWeek(S, monday, end, budget);
    if (w.days.length) out.push(w);
    monday = addDays(monday, -7);
  }
  return out;
}

/* ---------- words ---------- */

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
const NUM = ["no", "one", "two", "three", "four", "five", "six", "seven"];
const num = (n: number) => NUM[n] ?? String(n);
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const dayName = (ds: string) => DAY_NAME[parseDay(ds).getDay()];

function andList(xs: string[]): string {
  if (xs.length <= 1) return xs.join("");
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

/** "31 Aug – 6 Sep", "14 – 20 Sep"; `withDays` for the week in progress: "Mon 21 – Tue 22 Sep". */
export function weekRange(w: Pick<FoodWeek, "from" | "to">, withDays = false): string {
  const a = parseDay(w.from);
  const b = parseDay(w.to);
  const dow = (d: Date) => (withDays ? `${DOW_LABELS[d.getDay()]} ` : "");
  if (w.from === w.to) return withDays ? dayLabel(w.from) : dayMonth(w.from);
  if (a.getMonth() === b.getMonth()) return `${dow(a)}${a.getDate()} – ${dow(b)}${dayMonth(w.to)}`;
  return `${dow(a)}${dayMonth(w.from)} – ${dow(b)}${dayMonth(w.to)}`;
}

/** The meals logged on a day, as a sentence's subject: "Lunch", "Breakfast and lunch". */
function mealWords(meals: MealSlot[]): string {
  return cap(andList(meals.map((m) => MEAL_LABEL[m].toLowerCase())));
}

/** A day row's second line: why it is out, or that it was a travel day. Null when there is
 *  nothing to add to the date and the figure. */
export function weekDayLine(d: WeekDay): string | null {
  if (d.mark === "untracked")
    return d.kcal === null ? "Nothing logged" : `${mealWords(d.meals)} logged, ${fmt(d.kcal)} kcal; the rest wasn’t`;
  if (d.mark === "fast") return d.kcal === null ? "Nothing logged" : `${fmt(d.kcal)} kcal logged`;
  if (!d.complete) return "Nothing logged";
  return d.travel ? "Travel day" : null;
}

/** "5 complete days · 3 not logged", with the marks between when there are any. */
export function weekCountLine(w: FoodWeek): string {
  const parts = [`${w.complete} complete ${plural(w.complete, "day")}${w.current ? " so far" : ""}`];
  if (w.fast) parts.push(`${w.fast} fast ${plural(w.fast, "day")}`);
  if (w.untracked) parts.push(`${w.untracked} untracked`);
  if (w.unlogged && !w.current) parts.push(`${w.unlogged} not logged`);
  return parts.join(" · ");
}

/**
 * What the week says, in the insight shape every Progress page renders: the averages, the days
 * over budget, and — when any day is out — which days and why, so an average over five days is
 * never read as one over seven.
 */
export function weekInsights(w: FoodWeek): Insight[] {
  const out: Insight[] = [];
  const range = weekRange(w, true);
  const completeBasis = `${w.complete} complete ${plural(w.complete, "day")}`;

  if (w.avgKcal === null) {
    out.push({
      key: "week.avg",
      text: "No complete day this week, so there is no average — a day left out is never counted as zero.",
      basedOn: basedOn(`${w.days.length} ${plural(w.days.length, "day")}`, range),
    });
  } else {
    const on = w.complete === 1 ? "On the one complete day you ate" : `On the ${num(w.complete)} complete days you ate`;
    const protein = w.avgProtein === null ? "" : ` and ${fmt(w.avgProtein)} g protein`;
    out.push({
      key: "week.avg",
      text: `${on} ${fmt(w.avgKcal)} kcal${protein}${w.complete === 1 ? "" : " a day"}.`,
      basedOn: basedOn(completeBasis, range),
    });
  }

  if (w.judged) {
    const over = w.days.filter((d) => d.complete && d.over !== null);
    let text: string;
    if (!over.length) text = w.judged === 1 ? "The complete day was within budget." : "Every complete day was within budget.";
    else if (over.length === 1) {
      const d = over[0];
      text = `${dayName(d.ds)}${d.travel ? ", the travel day," : ""} was the one over budget — by ${fmt(d.over ?? 0)} kcal.`;
    } else if (over.length === w.judged || over.length > 3) {
      // Past three a list of days and figures stops being read; the count and the mean are the news.
      const by = over.reduce((a, d) => a + (d.over ?? 0), 0) / over.length;
      const which =
        over.length !== w.judged
          ? `${cap(num(over.length))} of ${w.judged} complete days`
          : w.judged === 2
            ? "Both complete days"
            : `All ${num(over.length)} complete days`;
      text = `${which} were over budget, by ${fmt(by)} kcal on average.`;
    } else {
      text = `${andList(over.map((d) => dayName(d.ds)))} were over budget — by ${andList(over.map((d) => fmt(d.over ?? 0)))} kcal.`;
    }
    out.push({ key: "week.over", text, basedOn: basedOn(`${w.judged} complete ${plural(w.judged, "day")}`) });
  }

  const left: string[] = [];
  const untracked = w.days.filter((d) => d.mark === "untracked");
  if (w.fast) left.push(w.fast === 1 ? "the fast day" : `the ${w.fast} fast days`);
  if (untracked.length) left.push(untracked.length === 1 ? `the untracked ${dayName(untracked[0].ds)}` : `the ${untracked.length} untracked days`);
  if (w.unlogged) left.push(w.unlogged === 1 ? "the day with nothing logged" : `the ${w.unlogged} days with nothing logged`);
  const n = w.fast + untracked.length + w.unlogged;
  if (n && w.complete) {
    out.push({
      key: "week.left",
      text: `${cap(andList(left))} ${n === 1 ? "is" : "are"} left out, not counted as zero, so the average is what you ate on days you logged in full.`,
      basedOn: basedOn(`${w.days.length} ${plural(w.days.length, "day")}`),
    });
  }
  return out;
}
